/* eslint-disable max-len,react/forbid-prop-types */
import React, { memo, useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';
import {
  Button,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { isValid, parse } from 'date-fns';
import BrokerDatePicker from '../../../../common/BrokerDatePicker';
import { get } from 'lodash';
import { PICKER_FORMAT_DATE } from '../../../../../utils/Constants';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(2),
    width: '100%',
  },
  formControl: {
    margin: theme.spacing(1),
  },
  addIcon: {
    color: '#90B648',
  },
  expansionPanelSummary: {
    backgroundColor: '#F4F4F4',
  },
  remove: {
    padding: 0,
    color: '#ef5350',
  },
}));

function MedicalConditions({ fieldArrayName, medicalConditionsValues = [], hasCovid }) {
  const { control, formState: { errors }, getValues, watch } = useFormContext();
  const { fields, append, remove } = useFieldArray(
    {
      control,
      name: fieldArrayName,
    },
  );
  const classes = useStyles();
  const formValues = getValues();
  const conditionsWatcher = watch(fieldArrayName);

  useEffect(() => {
    if (medicalConditionsValues?.length > 0) {
      setMainMedicalConditions(medicalConditionsValues);
    }
  }, [medicalConditionsValues]);


  const setMainMedicalConditions = (medicalConditions) => {
    const mainMedicalConditions = medicalConditions.map((item) => ({
      diagnosis: item.Diagnosis,
      diagnosisFrom: item.DiagnosisFrom,
      diagnosisIsToThePresent: item.DiagnosisIsToThePresent,
      diagnosisTo: item.DiagnosisTo,
    }));
    append(mainMedicalConditions);
  };

  const checkErrorProperty = (index, property) => {
    if (get(errors, fieldArrayName) && get(errors, fieldArrayName)[index]) {
      return get(errors, fieldArrayName)[index]?.hasOwnProperty(`${property}`);
    }
    return false;
  };

  const getMessage = (index, property) => {
    if (get(errors, fieldArrayName)
      && get(errors, fieldArrayName)[index]) {
      return get(errors, fieldArrayName)[index][property]?.message;
    }
    return '';
  };

  const nestedForm = getValues();

  useEffect(() => {
    const checkCovidOnArray = fields.some((f) => f.diagnosis === 'SARS-CoV-2/COVID-19');
    if (hasCovid && !checkCovidOnArray) {
      append({
        diagnosis: 'SARS-CoV-2/COVID-19',
        diagnosisFrom: null,
        diagnosisTo: null,
        diagnosisIsToThePresent: false,
      });
    } if (!hasCovid && fields.length > 0) {
      fields.map((item, index) => {
        if (item?.diagnosis === 'SARS-CoV-2/COVID-19') {
          remove(index);
        }
      });
    }
  }, [hasCovid]);


  return (
    <div className={classes.root}>
      <Grid container direction="row" spacing={2}>
        <Grid item xs={5}>
          <Typography className={classes.formControl}>DIAGNÓSTICO</Typography>
        </Grid>
      </Grid>
      {fields.map((item, index) => (
        <Accordion
          key={item.id}
          expanded
        >
          <AccordionSummary
            className={classes.expansionPanelSummary}
            aria-controls="panel-content"
            id="panel-header"
          >
            <Grid container direction="row" spacing={2}>
              <Grid item xs={10}>
                <Typography>{get(formValues, `${fieldArrayName}.${index}.diagnosis`) || 'Condición'}</Typography>
              </Grid>
              <Grid item xs={1}>
                <IconButton
                  aria-label="delete"
                  className={classes.remove}
                  onClick={() => remove(index)}
                  disabled={fields[index].diagnosis === 'SARS-CoV-2/COVID-19'}
                  size="large">
                  <RemoveCircleIcon className={classes.icon} />
                </IconButton>
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container direction="row" spacing={2}>
              <Grid
                item
                md={conditionsWatcher
                && !conditionsWatcher[index]?.diagnosisIsToThePresent ? 3 : 5}
                xs={12}
              >
                <Controller
                  name={`${fieldArrayName}.${index}.diagnosis`}
                  control={control}
                  defaultValue={fields[index].diagnosis}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id={`text${index}`}
                      className={classes.formControl}
                      fullWidth
                      label="Diagnóstico"
                      autoComplete="off"
                      error={checkErrorProperty(index, 'diagnosis')}
                      helperText={getMessage(index, 'diagnosis')}
                      disabled={fields[index].diagnosis === 'SARS-CoV-2/COVID-19'}
                    />
                  )}
                />
              </Grid>
              <Grid
                item
                md={conditionsWatcher
                && !conditionsWatcher[index]?.diagnosisIsToThePresent ? 3 : 4}
                xs={12}
              >
                <Controller
                  name={`${fieldArrayName}.${index}.diagnosisFrom`}
                  control={control}
                  defaultValue={fields[index].diagnosisFrom}
                  render={({ field }) => (
                    <BrokerDatePicker
                      value={field.value}
                      name={field.name}
                      onBlur={field.onBlur}
                      disableFuture
                      id={`pickerFromDate${index}`}
                      className={classes.formControl}
                      fullWidth
                      format={PICKER_FORMAT_DATE}
                      margin="normal"
                      label="Desde"
                      onChange={(formattedDate) => {
                        if (!formattedDate) {
                          field.onChange('Invalid');
                          return;
                        }
                        const parsed = parse(formattedDate, PICKER_FORMAT_DATE, new Date());
                        field.onChange(isValid(parsed) ? parsed.toISOString() : 'Invalid');
                      }}
                      error={checkErrorProperty(index, 'diagnosisFrom')}
                      helperText={getMessage(index, 'diagnosisFrom')}
                    />
                  )}
                />
              </Grid>
              <Grid item md={3} xs={12} container alignItems="flex-end">
                <FormControlLabel
                  control={(
                    <Controller
                      name={`${fieldArrayName}.${index}.diagnosisIsToThePresent`}
                      control={control}
                      defaultValue={fields[index]?.diagnosisIsToThePresent ?? false}
                      render={({ field }) => (
                        <Switch
                          {...field}
                          checked={!!field.value}
                          color="primary"
                        />
                      )}
                    />
                  )}
                  label={(
                    <Typography style={{ fontSize: 12 }}>
                      Actualidad
                    </Typography>
                  )}
                  labelPlacement="start"
                />
              </Grid>
              {conditionsWatcher
              && !conditionsWatcher[index]?.diagnosisIsToThePresent && (
                <Grid item md={3} xs={12}>
                  <Controller
                    name={`${fieldArrayName}.${index}.diagnosisTo`}
                    control={control}
                    defaultValue={fields[index].diagnosisTo}
                    render={({ field }) => (
                      <BrokerDatePicker
                        value={field.value}
                        name={field.name}
                        onBlur={field.onBlur}
                        disableFuture
                        id={`pickerToDate${index}`}
                        className={classes.formControl}
                        fullWidth
                        format={PICKER_FORMAT_DATE}
                        margin="normal"
                        label="Hasta"
                        minDate={get(nestedForm, fieldArrayName)[index]?.diagnosisFrom}
                        onChange={(formattedDate) => {
                          if (!formattedDate) {
                            field.onChange('Invalid');
                            return;
                          }
                          const parsed = parse(formattedDate, PICKER_FORMAT_DATE, new Date());
                          field.onChange(isValid(parsed) ? parsed.toISOString() : 'Invalid');
                        }}
                        error={checkErrorProperty(index, 'diagnosisTo')}
                        helperText={getMessage(index, 'diagnosisTo')}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
      >
        <Grid item md={12} xs={12}>
          <Button
            color="primary"
            disableElevation
            startIcon={<AddCircleIcon className={classes.addIcon} />}
            onClick={() => append({
              diagnosis: '', diagnosisFrom: null, diagnosisTo: null, diagnosisIsToThePresent: false,
            })}
          >
            Agregar Preexistencia
          </Button>
        </Grid>
      </Grid>
    </div>
  );
}

MedicalConditions.propTypes = {
  fieldArrayName: PropTypes.string.isRequired,
  medicalConditionsValues: PropTypes.array,
  hasCovid: PropTypes.bool.isRequired,
};

export default memo(MedicalConditions);
